import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { fetchProjectActivities } from "../../../api/project_activity";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

/**
 * Picker for project_activities (Promang activity lines), not legacy ActivityItems.
 */
export default function ModernProjectActivitySelector({
  open = false,
  selectedProjectActivities = [],
  onChange,
  onClose,
  multiple = false,
  project = null,
  showHeader = true,
  /** Merged into GET project_activities query (e.g. selectable_for_work_order, work_order_id) */
  listFetchParams = null,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedProjectActivities.map((a) => a.id))
  );

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedProjectActivities.map((a) => a.id)));
    }
  }, [selectedProjectActivities, open]);

  useEffect(() => {
    if (!open) {
      setRows([]);
      return;
    }
    if (!project?.id) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchProjectActivities(
      project.id,
      listFetchParams && Object.keys(listFetchParams).length > 0
        ? { params: listFetchParams }
        : {}
    )
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setError(t("project_activity.load_error"));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, project?.id, t, listFetchParams]);

  useEffect(() => {
    if (open) {
      setSearchQuery("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const title = (r.display_title || r.title || "").toLowerCase();
      const num = (r.number || "").toString().toLowerCase();
      return title.includes(q) || num.includes(q);
    });
  }, [rows, searchQuery]);

  const toggle = useCallback(
    (pa) => {
      const next = new Set(selectedIds);
      if (next.has(pa.id)) {
        next.delete(pa.id);
      } else {
        if (!multiple) next.clear();
        next.add(pa.id);
      }
      setSelectedIds(next);
      const selectedFromList = filtered.filter((a) => next.has(a.id));
      const selectedFromPrev = selectedProjectActivities.filter(
        (a) => next.has(a.id) && !filtered.find((x) => x.id === a.id)
      );
      const selected = [...selectedFromList, ...selectedFromPrev];
      if (onChange) onChange(selected);
      if (!multiple && next.size > 0 && onClose) {
        setTimeout(() => onClose(), 200);
      }
    },
    [selectedIds, filtered, selectedProjectActivities, multiple, onChange, onClose]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    if (onChange) onChange([]);
  }, [onChange]);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  if (!open) return null;

  if (!project?.id) {
    return (
      <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-center h-full p-6">
          <p className="text-sm text-gray-500">{t("project_activity.select_project_first")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border border-gray-200 shadow-sm max-h-[75vh]">
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {t("project_activity.dialog_title")}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title={t("project_activity.clear_selection")}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title={t("Close")}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("project_activity.search_placeholder")}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading && (
          <div className="p-6 text-center text-sm text-gray-500">{t("Loading")}…</div>
        )}
        {error && !isLoading && (
          <div className="p-6 text-center text-sm text-red-600">{error}</div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">
            {t("project_activity.empty")}
          </div>
        )}
        {!isLoading &&
          !error &&
          filtered.map((pa) => (
            <button
              key={pa.id}
              type="button"
              onClick={() => toggle(pa)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 flex items-center gap-3 hover:bg-gray-50 ${
                isSelected(pa.id) ? "bg-indigo-50" : ""
              }`}
            >
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded border border-gray-300">
                {isSelected(pa.id) ? (
                  <CheckIcon className="h-4 w-4 text-indigo-600" />
                ) : null}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900 truncate">
                  {pa.display_title || pa.title || `#${pa.id}`}
                </span>
                {pa.activity_type && pa.activity_type !== "standard" && (
                  <span className="block text-xs text-gray-500">{pa.activity_type}</span>
                )}
              </span>
            </button>
          ))}
      </div>
    </div>
  );
}
